import React, { useState } from 'react'
import {
  DraftStandardAudience,
  FieldworkAudienceType
} from '../../../data/model/audience'
import { useCreateAudienceMemberV2 } from '../../../hooks/audience/createAudienceMemberV2'
import { useRemoveAudienceMemberV2 } from '../../../hooks/audience/removeAudienceMemberV2'
import useStandardAudienceFilteredListGqlOperations from '../../../hooks/audience/useStandardAudienceFilteredListGqlOperations'
import { useDebounceEffect } from '../../../hooks/useDebounce'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { isFocaldataUser } from '../../../utils/authUtils'
import { TARGETING_CRITERIA_CODES_FOR_NON_FD_USERS } from '../constants'
import AudienceSearchControl, {
  AudienceSearchResult
} from './AudienceSearch.control'

const AudienceSearch: React.FC = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const [results, setResults] = useState<AudienceSearchResult[]>()
  const [searchCriteria, setSearchCriteria] = useState<string>('')
  const {
    searchCategories,
    loadingSearchAudience,
    audienceQuestions,
    refetchCategories
  } = useStandardAudienceFilteredListGqlOperations(searchCriteria)

  const createAudienceMemberV2 = useCreateAudienceMemberV2()
  const removeAudienceMemberV2 = useRemoveAudienceMemberV2()

  const isCriterionCodeSelected: (code: string) => boolean = (code) => {
    return !!audienceQuestions?.members.find(
      (standardAudienceQuestion) =>
        standardAudienceQuestion.memberType ===
          FieldworkAudienceType.Standard &&
        (standardAudienceQuestion.memberItem as DraftStandardAudience).criterion
          .code === code
    )
  }

  const formatResults: () => AudienceSearchResult[] | undefined = () => {
    if (searchCategories) {
      const res: AudienceSearchResult[] = searchCategories
        // Filter out targeting criteria that should not be shown to non-Focaldata users
        .filter((c) => {
          if (c.category.code === 'business_&_occupation') {
            return (
              isFocaldataUser() ||
              TARGETING_CRITERIA_CODES_FOR_NON_FD_USERS.has(
                c.standardCriteria.criterion.code
              )
            )
          } else return true
        })
        .map(({ standardCriteria, category }) => {
          const {
            criterion: { code, name, question },
            options
          } = standardCriteria
          return {
            code,
            name,
            category: category.name,
            question: question.text,
            questionId: question.questionId,
            responses: options.map(({ responseOption }) => ({
              text: responseOption.value,
              responseOptionId: responseOption.responseOptionId
            })),
            selected: isCriterionCodeSelected(code)
          }
        })
      return res
    }
    return undefined
  }

  useDebounceEffect<string | undefined>(
    () => {
      if (searchCriteria) {
        refetchCategories({ searchCriteria })
      }
    },
    searchCriteria,
    { delay: 1500 }
  )

  const toggleResultSelection: (result: AudienceSearchResult) => void = (
    result
  ) => {
    if (results) {
      const newResults = [...results]
      const target = newResults.find((res) => res.code === result.code)
      if (target) {
        target.selected = !target.selected
      }

      setResults(newResults)
    }
  }

  const handleResultClick: (result: AudienceSearchResult) => void = (
    result
  ) => {
    if (!result.selected) {
      const audienceQuestionsNr: number | undefined =
        audienceQuestions?.members.length
      createAudienceMemberV2({
        variables: {
          input: {
            projectId,
            surveyId,
            position: audienceQuestionsNr || 0,
            audienceCriterionCode: result.code
          }
        }
      })
    } else {
      removeAudienceMemberV2(result.questionId)
    }

    toggleResultSelection(result)
  }

  const handleSearchCriteriaChange = (newSearchCriteria: string) => {
    setSearchCriteria(newSearchCriteria)
  }

  return (
    <AudienceSearchControl
      loading={loadingSearchAudience}
      results={formatResults()}
      onChange={handleSearchCriteriaChange}
      onResultClick={handleResultClick}
    />
  )
}

export default AudienceSearch
